import React from 'react';
import values from 'lodash/values';
import get from 'lodash/get';
import omit from 'lodash/omit';
import has from 'lodash/has';
import findIndex from 'lodash/findIndex';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import { withTabs } from '@skunexus/component-catalog';
import MainTabs from './components/InterOfficeMainTabsLayout';
import InterOfficeLabel from './packages/ShipmentPrint/components/InterOfficeLabel';

import { filterAccessibleItems } from '../../../utils';
import UserModel from '../../../UserModel';

import {
    TITLE_PREFIX,
    DOMAIN_NAME,
    WORKFLOW_NAME,
    DASHBOARD_ROUTE,
    PICK_ROUTE,
    GROUP_PICK_ROUTE,
    PACK_ROUTE,
    READY_FOR_PICKUP_ROUTE,
    FINALIZED_ROUTE,
    ORDER_DETAILS_ROUTE,
    CANCEL_ROUTE,
    DETAILS_ROUTE,
    GROUP_PICK_DETAILS_ROUTE,
    PICKING_ROUTE,
    GROUP_PICKING_ROUTE,
    PACKING_ROUTE,
    PRODUCTS_DETAILS_ROUTE,
    USERS_DETAILS_ROUTE,
    STATES,
} from './consts';

export default (app) => {
    const tabs = app.interOfficeTabs.getAll();

    const isConfirmPickupAvailable = () => false;

    const isPrintDetailsAvailable = (details) => {
        const state = get(details, 'current_state');

        return !(state === STATES.pick || state === STATES.pack);
    };

    const routers = {
        interOfficeDetails: {
            path: `${DETAILS_ROUTE}/:id`,
            title: `${TITLE_PREFIX} > Details`,
            permission: ['fulfillments.show'],
            props: () => ({
                detailsPageRoute: DETAILS_ROUTE,
                pickingPageRoute: PICKING_ROUTE,
                packingPageRoute: PACKING_ROUTE,
                packPageRoute: PACK_ROUTE,
                cancelPageRoute: CANCEL_ROUTE,
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                productDetailsPageRoute: PRODUCTS_DETAILS_ROUTE,
                finalizedPageRoute: FINALIZED_ROUTE,
                currentUserId: UserModel.getUserId(),
                domain: DOMAIN_NAME,
                isConfirmPickupAvailable,
                isPrintDetailsAvailable,
                actionsBarProps: {
                    printAvailableStates: [STATES.pick],
                    pickingAvailableStates: [STATES.pick],
                    packingAvailableStates: [STATES.pack],
                },
                printShippingProps: {
                    config: {
                        showLabel: true,
                        labelComponent: InterOfficeLabel,
                    },
                },
            }),
            loader: () => import('@skunexus/interoffice-details'),
        },
        interOfficeGroupPickDetails: {
            path: `${GROUP_PICK_DETAILS_ROUTE}/:id`,
            title: `${TITLE_PREFIX} > Group Pick Details`,
            permission: ['fulfillments.picking'],
            props: () => ({
                pickingPageRoute: GROUP_PICKING_ROUTE,
                productDetailsPageRoute: PRODUCTS_DETAILS_ROUTE,
                currentUserId: UserModel.getUserId(),
                domain: DOMAIN_NAME,
            }),
            loader: () => import('@skunexus/interoffice-group-pick-details'),
        },
        interOfficeGroupPicking: {
            path: `${GROUP_PICKING_ROUTE}/:id`,
            title: `${TITLE_PREFIX} > Group Picking`,
            permission: ['fulfillments.picking'],
            props: () => ({
                detailsPageRoute: GROUP_PICK_DETAILS_ROUTE,
                packPageRoute: PACK_ROUTE,
                groupPickPageRoute: GROUP_PICK_ROUTE,
                productDetailsRoute: PRODUCTS_DETAILS_ROUTE,
                userDetailsPageRoute: USERS_DETAILS_ROUTE,
                domain: DOMAIN_NAME,
                correctOrderRequired: false,
                withLocationScanner: false,
            }),
            loader: () => import('@skunexus/interoffice-group-picking'),
        },
        interOfficeCancel: {
            path: `${CANCEL_ROUTE}/:id`,
            title: `${TITLE_PREFIX} > Cancel`,
            permission: ['fulfillments.index'],
            props: () => ({
                domain: DOMAIN_NAME,
                packPageRoute: PACK_ROUTE,
                packingPageRoute: PACKING_ROUTE,
                pickPageRoute: PICK_ROUTE,
                pickingPageRoute: PICKING_ROUTE,
                dashboardPageRoute: DASHBOARD_ROUTE,
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                productDetailsPageRoute: PRODUCTS_DETAILS_ROUTE,
            }),
            loader: () => import('@skunexus/interoffice-cancel'),
        },
        interOfficePicking: {
            path: `${PICKING_ROUTE}/:id`,
            title: `${TITLE_PREFIX} > Picking`,
            permission: ['fulfillments.picking'],
            props: () => ({
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                detailsPageRoute: DETAILS_ROUTE,
                pickingPageRoute: PICKING_ROUTE,
                packingPageRoute: PACKING_ROUTE,
                pickPageRoute: PICK_ROUTE,
                cancelPageRoute: CANCEL_ROUTE,
                productDetailsPageRoute: PRODUCTS_DETAILS_ROUTE,
                userDetailsPageRoute: USERS_DETAILS_ROUTE,
                shipmentDetailsPageRoute: '',
                domain: DOMAIN_NAME,
                states: STATES,
                currentUserId: UserModel.getUserId(),
                correctOrderRequired: false,
                withLocationScanner: false,
            }),
            loader: () => import('@skunexus/interoffice-picking'),
        },
        interOfficePacking: {
            path: `${PACKING_ROUTE}/:id`,
            title: `${TITLE_PREFIX} > Packing`,
            permission: ['fulfillments.packing'],
            props: () => ({
                detailsPageRoute: DETAILS_ROUTE,
                pickingPageRoute: PICKING_ROUTE,
                cancelPageRoute: CANCEL_ROUTE,
                productDetailsPageRoute: PRODUCTS_DETAILS_ROUTE,
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                domain: DOMAIN_NAME,
                states: STATES,
                currentUserId: UserModel.getUserId(),
            }),
            loader: () => import('@skunexus/interoffice-packing'),
        },
    };

    if (tabs.finalized) {
        routers.interOfficeFinalized = {
            path: tabs.finalized.link,
            title: tabs.finalized.title,
            permission: tabs.finalized.permission,
            props: () => ({
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                detailsPageRoute: DETAILS_ROUTE,
                gridConfig: {
                    id: 'inter-office-finalized-grid',
                    domain: DOMAIN_NAME,
                    state: 'finalized',
                },
            }),
            loader: () => import('@skunexus/interoffice-fulfilled'),
            render: (Component) => (props) =>
                (
                    <MainTabs showCartFilter={false} {...props} name="interOfficeFinalized">
                        <Component {...props} />
                    </MainTabs>
                ),
        };
    }
    if (tabs.groupPick) {
        routers.interOfficeGroupPick = {
            path: tabs.groupPick.link,
            title: tabs.groupPick.title,
            permission: tabs.groupPick.permission,
            props: () => ({
                detailsPageRoute: GROUP_PICK_DETAILS_ROUTE,
                gridConfig: {
                    id: 'inter-office-group-pick-grid',
                    domain: DOMAIN_NAME,
                },
            }),
            loader: () => import('@skunexus/interoffice-group-pick'),
            render: (Component) => (props) =>
                (
                    <MainTabs {...props} name="interOfficeGroupPick">
                        <Component {...props} />
                    </MainTabs>
                ),
        };
    }
    if (tabs.main) {
        const interOfficeMainCustomGridSections = (config) => {
            const newConfig = cloneDeep(config);
            newConfig.sections = {
                ...newConfig.sections,
                pick: {
                    ...newConfig.sections.pick,
                    path: PICK_ROUTE,
                },
                pack: {
                    ...newConfig.sections.pack,
                    path: PACK_ROUTE,
                },
                ready: {
                    title: 'Shipped',
                    path: READY_FOR_PICKUP_ROUTE,
                    order: 30,
                },
                finalized: {
                    title: 'Finalized',
                    path: FINALIZED_ROUTE,
                    order: 40,
                },
            };
            newConfig.sections = omit(newConfig.sections, ['dispatch', 'fulfilled']);
            newConfig.domain = DOMAIN_NAME;
            return newConfig;
        };

        routers.interOfficeMain = {
            path: tabs.main.link,
            title: tabs.main.title,
            permission: tabs.main.permission,
            props: () => ({
                customGridSections: interOfficeMainCustomGridSections,
                detailsPageRoute: DETAILS_ROUTE,
            }),
            loader: () => import('@skunexus/ship-main'),
            render: (Component) => (props) =>
                (
                    <MainTabs showCartFilter={false} showAssignUserFilter={false} {...props} name="interOfficeMain">
                        <Component {...props} />
                    </MainTabs>
                ),
        };
    }
    if (tabs.pack) {
        routers.interOfficePack = {
            path: tabs.pack.link,
            title: tabs.pack.title,
            permission: tabs.pack.permission,
            props: () => ({
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                packingPageRoute: PACKING_ROUTE,
                gridConfig: {
                    id: 'inter-office-pack-grid',
                    domain: DOMAIN_NAME,
                },
                currentUserId: UserModel.getUserId(),
            }),
            loader: () => import('@skunexus/interoffice-pack'),
            render: (Component) => (props) =>
                (
                    <MainTabs {...props} name="interOfficePack">
                        <Component {...props} />
                    </MainTabs>
                ),
        };
    }
    if (tabs.pick) {
        routers.interOfficePick = {
            path: tabs.pick.link,
            title: tabs.pick.title,
            permission: tabs.pick.permission,
            props: () => ({
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                detailsPageRoute: DETAILS_ROUTE,
                groupPageRoute: GROUP_PICKING_ROUTE,
                gridConfig: {
                    id: 'inter-office-pick-grid',
                    domain: DOMAIN_NAME,
                    workflow: WORKFLOW_NAME,
                },
                currentUserId: UserModel.getUserId(),
            }),
            loader: () => import('@skunexus/interoffice-pick'),
            render: (Component) => (props) =>
                (
                    <MainTabs {...props} name="interOfficePick">
                        <Component {...props} />
                    </MainTabs>
                ),
        };
    }

    if (tabs.waves) {
        const customWaveGridConfig = (data) => {
            if (!has(data.config.filtering, 'state')) {
                set(data, 'config.filtering.state', {
                    operator: 'in',
                    value: ['not_picked', 'partial_picked'],
                });
            }
            return {
                ...data,
            };
        };
        routers.interOfficeWaves = {
            path: tabs.waves.link,
            title: tabs.waves.title,
            permission: tabs.waves.permission,
            props: () => ({
                detailsPageRoute: GROUP_PICK_DETAILS_ROUTE,
                gridConfig: {
                    id: 'fulfillment-interoffice-wave-grid',
                    domain: DOMAIN_NAME,
                    workflow: WORKFLOW_NAME,
                },
                customGridConfig: customWaveGridConfig,
            }),

            loader: () => import('@skunexus/interoffice-waves'),
            render: (Component) => (props) =>
                (
                    <MainTabs {...props} name="interOfficeWaves">
                        {withTabs(filterAccessibleItems(values(app.interOfficeWavesTabs.getAll())), {
                            sub: true,
                        })(Component)(props)}
                    </MainTabs>
                ),
        };

        const customWaveHistoryGridData = (data) => {
            const stateColIndex = findIndex(get(data, 'columns', []), ['field', 'state']);

            if (stateColIndex >= 0) {
                set(data, `columns[${stateColIndex}].isFilterable`, false);
                set(data, `columns[${stateColIndex}].defaultFilter`, ['picked']);
                set(data, `columns[${stateColIndex}].options`, [{ label: 'Picked', value: 'picked' }]);
            }

            return {
                ...data,
            };
        };

        const customWaveHistoryGridConfig = (data) => {
            set(data, 'config.filtering.state', {
                operator: 'in',
                value: ['picked'],
            });
            return {
                ...data,
            };
        };

        routers.interOfficeWavesHistory = {
            path: app.interOfficeWavesTabs.getItem('wavesHistory').link,
            title: app.interOfficeWavesTabs.getItem('wavesHistory').title,
            permission: app.interOfficeWavesTabs.getItem('wavesHistory').permission,
            props: () => ({
                detailsPageRoute: GROUP_PICK_DETAILS_ROUTE,
                gridConfig: {
                    id: 'fulfillment-interoffice-wave-history-grid',
                    domain: DOMAIN_NAME,
                    workflow: WORKFLOW_NAME,
                },
                releaseWaveEnabled: false,
                customGridData: customWaveHistoryGridData,
                customGridConfig: customWaveHistoryGridConfig,
            }),
            loader: () => import('@skunexus/interoffice-waves'),
            render: (Component) => (props) =>
                (
                    <MainTabs {...props} name="interOfficeWavesHistory">
                        {withTabs(filterAccessibleItems(values(app.interOfficeWavesTabs.getAll())), {
                            sub: true,
                        })(Component)(props)}
                    </MainTabs>
                ),
        };
    }

    if (tabs.ready) {
        routers.interOfficeReady = {
            path: tabs.ready.link,
            title: tabs.ready.title,
            permission: tabs.ready.permission,
            props: () => ({
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                detailsPageRoute: DETAILS_ROUTE,
                gridConfig: {
                    id: 'inter-office-ready-grid',
                    domain: DOMAIN_NAME,
                },
            }),
            loader: () => import('@skunexus/interoffice-ready'),
            render: (Component) => (props) =>
                (
                    <MainTabs showCartFilter={false} {...props} name="interOfficeReady">
                        <Component {...props} />
                    </MainTabs>
                ),
        };
    }

    if (tabs.cancelled) {
        routers.interOfficCancelled = {
            path: tabs.cancelled.link,
            title: tabs.cancelled.title,
            permission: tabs.cancelled.permission,
            props: () => ({
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                detailsPageRoute: DETAILS_ROUTE,
                gridConfig: {
                    id: 'inter-office-cancelled-grid',
                    domain: DOMAIN_NAME,
                },
            }),
            loader: () => import('@skunexus/interoffice-cancelled'),
            render: (Component) => (props) =>
                (
                    <MainTabs {...props} name="interOfficCancelled">
                        <Component {...props} />
                    </MainTabs>
                ),
        };
    }

    if (tabs.hospital) {
        routers.interOfficeHospital = {
            path: tabs.hospital.link,
            title: tabs.hospital.title,
            permission: tabs.hospital.permission,
            props: () => ({
                orderDetailsPageRoute: ORDER_DETAILS_ROUTE,
                detailsPageRoute: DETAILS_ROUTE,
                gridConfig: {
                    id: 'inter-office-hospital-grid',
                    domain: DOMAIN_NAME,
                },
            }),
            loader: () => import('@skunexus/interoffice-hospital'),
            render: (Component) => (props) =>
                (
                    <MainTabs {...props} name="interOfficeHospital">
                        <Component {...props} />
                    </MainTabs>
                ),
        };
    }

    return routers;
};
